import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { Multisite } from '@df/core/multisite/multisite.interface';
import { MultisiteService } from '@df/core/service/multisite';
import { ImageModule } from '@df/module-image/image.module';
import { DfUiModule } from '@df/ui/df-ui.module';
import { DfCmsModal } from '@df/ui/modal/df-cms-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { MultisiteRedirectService } from '@df/ui/multisite/multisite-redirect.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { merge } from 'rxjs';
import { ModalLayoutRightComponent, NModalLayoutRight } from '../../../ui/modal/modal-layout-right.component';
import { NMultisiteSwitchModal } from './multisite-switch-modal.interfaces';

export const storeOptionsFn = () =>
    (window['app'].multisite as MultisiteService).all.map(i => ({
        key: i.siteId,
        title: i.name
    }));
@DfCmsModal({
    id: NMultisiteSwitchModal.componentId,
    name: 'Multisite Switch Popup',
    position: 'custom',
    config: {
        ...NModalLayoutRight.ModalConfig,
        maxWidth: '100vw',
        panelClass: ['bg-col-w', 'overflow-auto']
    },
    blockGroup: 'modals',
    priority: 30,
    reopenPolicy: NDfModal.ReopenPolicy.first,
    sizeRestriction: () => ['S', 'M', 'L', 'X']
})
@ICmsComponent<MultisiteSwitchModalComponent>({
    name: 'Multisite Switch Popup',
    description: 'Appears when user wants to change store.',
    id: NMultisiteSwitchModal.componentId,
    block: {
        url: NMultisiteSwitchModal.componentId,
        scope: 'website'
    },
    onDemandOnly: true,
    fields: [
        {
            type: NICmsContentField.EFieldType.text,
            key: 'title',
            title: 'Title',
            required: true
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'subtitle',
            title: 'Subtitle',
            required: false
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'closeBtnCopy',
            title: 'Close button copy',
            required: false
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'stores',
            title: 'Store links',
            itemName: 'Store link',
            required: true,
            defaultSize: 3,
            fields: [
                <NICmsContentField.IChoiceField>{
                    type: NICmsContentField.EFieldType.choice,
                    key: 'siteId',
                    title: 'Website',
                    description: 'Please select website to link to. If website is off the link will be skipped.',
                    options: storeOptionsFn,
                    required: true
                },
                {
                    type: NICmsContentField.EFieldType.image,
                    key: 'icon',
                    title: 'Icon',
                    required: false
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'title',
                    title: 'Title',
                    required: true
                }
            ]
        }
    ]
})
@Component({
    selector: 'multisite-switch-modal',
    templateUrl: './multisite-switch-modal.component.html',
    styleUrls: ['./multisite-switch-modal.component.scss'],
    standalone: true,
    imports: [ModalLayoutRightComponent, ICmsClientModule, DfUiModule, ImageModule, FormsModule, TranslationModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultisiteSwitchModalComponent extends UiComponent implements OnInit, NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    content: NMultisiteSwitchModal.IBlockContent;

    protected multisiteRedirectService = inject(MultisiteRedirectService);

    private meta = inject(Meta);

    protected multisiteService = inject(MultisiteService);

    private app = inject(App);

    private modalRef = inject(ModalRef<any>);

    /**
     * Currently selected store
     */
    selectedStore?: string;

    /**
     * List of stores
     * only enabled ones are allowed
     */
    get stores() {
        return this.content.stores.filter(i => !!this.multisiteService.toMultisiteItem(i.siteId)) || [];
    }
    /**
     * Retrieves store url
     */
    getStoreUrl(store: NMultisiteSwitchModal.IBlockContentStore): string {
        return this.multisiteRedirectService.getLink(store.siteId);
    }

    goTo(siteId: number | string, $event?: Event) {
        if (this.multisiteRedirectService.to(siteId)) {
            $event?.preventDefault();
        } else {
            this.close();
        }
    }

    onStoreChange() {
        if (this.selectedStore) {
            this.goTo(this.selectedStore);
        }
    }

    override ngOnInit() {
        this.subscriptions.add(
            merge(this.app.events.on(Multisite.META_ALTERNATE_UPDATE_EVENT), this.meta.subject).subscribe(() => this.detectChanges())
        );
        super.ngOnInit();
    }

    close() {
        this.modalRef.close();
    }
}
